import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Loader2 } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useAtendentes } from '@/hooks/useProfiles';
import { useTransferirAtendimento } from '@/hooks/useClientes';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

interface TransferirAtendimentoButtonProps {
  clienteId: string;
  responsavelId?: string | null;
  responsavelNome?: string | null;
}

/**
 * Botão de transferência de atendimento para o header da conversa no Painel.
 * Visível apenas para admin ou para o responsável atual do cliente. Reaproveita
 * o hook useTransferirAtendimento (mesma lógica do detalhe do cliente).
 */
export function TransferirAtendimentoButton({
  clienteId, responsavelId, responsavelNome,
}: TransferirAtendimentoButtonProps) {
  const { t } = useTranslation();
  const { profile, isAdmin } = useAuthContext();
  const { data: atendentes } = useAtendentes();
  const transferir = useTransferirAtendimento();
  const [open, setOpen] = useState(false);
  const [novoResponsavelId, setNovoResponsavelId] = useState('');

  const podeTransferir =
    !!responsavelId && !!profile?.id && (isAdmin || responsavelId === profile.id);
  if (!podeTransferir) return null;

  const atendentesDisponiveis = (atendentes ?? []).filter((a) => a.id !== responsavelId);

  const handleTransferir = () => {
    if (!profile?.id || !novoResponsavelId) return;
    const novoAtendente = atendentes?.find((a) => a.id === novoResponsavelId);
    if (!novoAtendente) return;

    transferir.mutate(
      {
        clienteId,
        novoResponsavelId,
        novoResponsavelNome: novoAtendente.nome,
        responsavelAnteriorId: responsavelId,
        responsavelAnteriorNome: responsavelNome ?? null,
        profileId: profile.id,
        profileNome: profile.nome,
      },
      {
        onSuccess: () => {
          setOpen(false);
          setNovoResponsavelId('');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="painel__icon-btn"
          title={t('clientPotential.transfer')}
          aria-label={t('clientPotential.transfer')}
        >
          <ArrowRightLeft size={16} />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('clientPotential.transfer')}</DialogTitle>
          <DialogDescription>{t('clientPotential.transferTitle')}</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('clientPotential.responsible')}:{' '}
            <strong>{responsavelNome || t('common.unknown')}</strong>
          </p>
          <div className="space-y-2">
            <Label>{t('clientPotential.responsible')}</Label>
            <Select value={novoResponsavelId} onValueChange={setNovoResponsavelId}>
              <SelectTrigger>
                <SelectValue placeholder={t('clientPotential.selectNewResponsible')} />
              </SelectTrigger>
              <SelectContent>
                {atendentesDisponiveis.map((atendente) => (
                  <SelectItem key={atendente.id} value={atendente.id}>
                    {atendente.nome} {atendente.role === 'admin' ? '(Admin)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleTransferir}
            disabled={!novoResponsavelId || transferir.isPending}
          >
            {transferir.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t('clientPotential.confirmTransfer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
